import { useRollbar } from '@rollbar/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { sortBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { Industry } from '../../models/Industry'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { isValidEmailAddress } from '../../utils'
import { PREMIUM_PLUS_PRICE, PREMIUM_PRICE, PREMIUM_SUITE_PRICE } from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import { SelectList } from '../common/SelectList'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

export const SignupWorkflow = () => {
    const navigate = useNavigate()
    const me = PSApplication.me()

    const queryClient = useQueryClient()
    const rollbar = useRollbar()

    const [isSigningUp, setIsSigningUp] = useState(false)

    const [selectedType, setSelectedType] = useState<'business' | 'mle' | null>(null)
    const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(me.industry ?? null)
    const [email, setEmail] = useState<string>('')
    const [businessName, setBusinessName] = useState('')
    const [zipCode, setZipCode] = useState('')
    const [selectedPlanID, setSelectedPlanID] = useState<string | null>(null)
    const [selectedAssociatedAccountUserIDs, setSelectedAssociatedAccountUserIDs] = useState<
        string[]
    >([])

    const [searchParams, setSearchParams] = useSearchParams()

    const { data: industries } = useQuery({
        queryKey: ['industries'],
        queryFn: () => RecordFactory.getInstance().loadIndustries(),
    })
    const sortedIndustries = sortBy(industries, (industry) => industry.name)
    const industryItems = sortedIndustries.map((industry) => ({
        id: industry.id ?? '',
        title: industry.name ?? '',
        industry,
    }))

    const { data: associatedAccountUsers } = useQuery({
        queryKey: ['associatedAccountUsers'],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchRoles('switch')
            if (!result.ok) {
                throw result.error
            }
            return result.value
        },
    })
    const associatedAccountUsersItems = associatedAccountUsers
        ?.filter((user) => user.solo || user.company)
        .map((user) => ({
            id: user.id ?? '',
            title: user.entity ?? user.name ?? '',
            subtitle: user.roleSubtitle ?? '',
            tertiaryView: <ContactAvatar user={user} />,
            user,
        }))
    const selectedAssociatedAccountUserItems =
        associatedAccountUsersItems?.filter((item) =>
            selectedAssociatedAccountUserIDs.includes(item.id)
        ) ?? []
    const handleSelectAssociatedAccountItems = (
        items: NonNullable<typeof associatedAccountUsersItems>
    ) => {
        setSelectedAssociatedAccountUserIDs(items.map((item) => item.id))
    }

    const step = useMemo(() => {
        const step = searchParams.get('step')
        switch (step) {
            case 'business':
            case 'rollup':
            case 'plan': {
                return step
            }
            default:
                return 'type'
        }
    }, [searchParams])

    usePageTitle('New account')

    const selectedTypeItem = typeItems.find((item) => item.id === selectedType)
    const handleSelectType = (items: typeof typeItems) => {
        const newID = items[0]?.id
        setSelectedType(newID === 'business' ? 'business' : newID === 'mle' ? 'mle' : null)
    }

    const selectedPlanItem = planItems.find((item) => item.id === selectedPlanID)
    const handleSelectPlan = (items: typeof planItems) => {
        const newID = items[0]?.id
        setSelectedPlanID(newID)
    }

    const canViewTypeStep =
        associatedAccountUsers === undefined ||
        (associatedAccountUsers.some((user) => user.solo || user.company) &&
            associatedAccountUsers.filter((user) => user.partner && user.allowRollup).length < 2)
    const canAdvancePastTypeStep = selectedType !== null
    const canAdvancePastBusinessStep =
        selectedIndustry !== null && email !== '' && isValidEmailAddress(email)
    && businessName !== '' && (selectedType === 'mle' || zipCode !== '')
    const canAdvancePastPlanStep = selectedPlanID !== null
    const canAdvancePastRollupStep = selectedAssociatedAccountUserIDs.length > 0

    // If we somehow land on some other step than 'type' without the proper info filled out,
    // this effect kicks us back to the first step.
    useEffect(() => {
        if (step !== 'type' && !canAdvancePastTypeStep) {
            setSearchParams({}, { replace: true })
        }
    }, [step, canAdvancePastTypeStep, setSearchParams])
    // If we're not allowed to view the 'type' step, this effect jumps to the next step.
    useEffect(() => {
        if (step === 'type' && !canViewTypeStep) {
            setSelectedType('business')
            setSearchParams({ step: 'business' }, { replace: true })
        }
    }, [step, canViewTypeStep, setSearchParams])

    const title = (() => {
        switch (step) {
            case 'type': {
                return 'What kind of account do you want to create?'
            }
            case 'business': {
                return selectedType === 'business'
                    ? 'Setup your business'
                    : 'Setup your multi-location rollup'
            }
            case 'plan': {
                return 'Choose your plan'
            }
            case 'rollup': {
                return 'Select the accounts to rollup'
            }
        }
    })()

    const handleSignup = async () => {
        setIsSigningUp(true)

        const newUser = new User({
            type: selectedType === 'business' ? 'solo' : 'partner',
            postal_code: zipCode,
            entity: businessName,
            name: me.name,
            email,
            industry: selectedIndustry,
            login: me.login ?? me.id,
            phone: me.phone,
            plan: selectedPlanID,
            allow_payment: true,
            allow_invoice: true,
            allow_booking: true,
            timezone: me.timezone,
        })

        const signupParams:
            | { type: 'business'; planID: string }
            | { type: 'mle'; associatedAccountUserIDs: string[] }
            | null =
            selectedType === 'business'
                ? selectedPlanID !== null
                    ? {
                          type: 'business',
                          planID: selectedPlanID,
                      }
                    : null
                : {
                      type: 'mle',
                      associatedAccountUserIDs: selectedAssociatedAccountUserIDs,
                  }
        if (signupParams === null) {
            setIsSigningUp(false)
            return
        }

        const loginResponse = await RecordFactory.getInstance().signup(
            newUser,
            signupParams,
            queryClient,
            rollbar
        )

        if (!loginResponse.ok) {
            setIsSigningUp(false)
            return
        }

        if (
            loginResponse.value.login.role === 'owner' ||
            loginResponse.value.login.role === 'admin'
        ) {
            window.location.href = '/'
        }
        else if (loginResponse.value.login.plan === 'franchise') {
            window.location.href = '/reports'
        }
        else {
            window.location.href = '/clients'
        }
    }

    return (
        <WorkflowPage
            title={title}
            onClose={{
                action: 'go-back',
                excludePathname: /^\/signup.*/,
                defaultPathname: '/',
            }}
            backButtonTitle={
                !canViewTypeStep && step === 'business'
                    ? undefined
                    : step === 'business'
                      ? 'Account type'
                      : step === 'plan' || step === 'rollup'
                        ? 'New account'
                        : undefined
            }
            onBack={!canViewTypeStep && step === 'business' ? undefined : () => navigate(-1)}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    {step === 'type' ? (
                        <Button
                            intent="primary"
                            onClick={() => setSearchParams({ step: 'business' })}
                            disabled={!canAdvancePastTypeStep}
                        >
                            Next
                        </Button>
                    ) : step === 'business' ? (
                        <Button
                            intent="primary"
                            onClick={() =>
                                setSearchParams({
                                    step: selectedType === 'business' ? 'plan' : 'rollup',
                                })
                            }
                            disabled={!canAdvancePastBusinessStep}
                        >
                            Next
                        </Button>
                    ) : step === 'plan' ? (
                        <Button
                            intent="primary"
                            onClick={handleSignup}
                            disabled={!canAdvancePastPlanStep}
                            isLoading={isSigningUp}
                        >
                            Start your 30-day free trial
                        </Button>
                    ) : step === 'rollup' ? (
                        <Button
                            intent="primary"
                            onClick={handleSignup}
                            disabled={!canAdvancePastRollupStep}
                            isLoading={isSigningUp}
                        >
                            Create rollup account
                        </Button>
                    ) : null}
                </div>
            }
        >
            <WorkflowPage.Card className="mt-12">
                {step === 'type' ? (
                    <div>
                        <SelectList
                            selectionType="single"
                            items={typeItems}
                            selectedItems={selectedTypeItem !== undefined ? [selectedTypeItem] : []}
                            onSelectItems={handleSelectType}
                        />

                        <div className="mt-3 -mb-4 flex items-center justify-center">
                            <a href="https://support.pocketsuite.io/hc/en-us/articles/36369855448973-Account-Switcher" target="_blank" rel="noreferrer">
                                <Button intent="tertiary" size="small">
                                    Learn more <Icon icon="externalLink.small" />
                                </Button>
                            </a>
                        </div>
                    </div>
                ) : step === 'business' ? (
                    <div className="flex flex-col items-stretch justify-start gap-y-8">
                        <WorkflowPage.IconSection title="Industry" icon="settings.large">
                            <SearchableSelect
                                items={industryItems}
                                selectedItemID={selectedIndustry?.id}
                                onSelectItem={(item) => setSelectedIndustry(item?.industry ?? null)}
                                placeholder="Select an industry"
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection title="Email" icon="envelope.large">
                            <TextField
                                type="email"
                                size="medium"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                onClear={() => setEmail('')}
                                placeholder="Enter email"
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection title="Business name" icon="briefcase.large">
                            <TextField
                                size="medium"
                                value={businessName}
                                onChange={(event) => setBusinessName(event.target.value)}
                                onClear={() => setBusinessName('')}
                                placeholder="Enter business name"
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection title="Zip code" icon="hash.large">
                            <TextField
                                size="medium"
                                value={zipCode}
                                mask="00000"
                                onChange={(event) => setZipCode(event.target.value)}
                                onClear={() => setZipCode('')}
                                placeholder="Enter zip code"
                            />
                        </WorkflowPage.IconSection>
                    </div>
                ) : step === 'rollup' ? (
                    <div>
                        {associatedAccountUsersItems !== undefined ? (
                            <SelectList
                                selectionType="multi"
                                items={associatedAccountUsersItems}
                                selectedItems={selectedAssociatedAccountUserItems}
                                onSelectItems={handleSelectAssociatedAccountItems}
                            />
                        ) : (
                            <div className="flex items-center justify-center">
                                <Spinner size={64} />
                            </div>
                        )}
                    </div>
                ) : step === 'plan' ? (
                    <SelectList
                        selectionType="single"
                        items={planItems}
                        selectedItems={selectedPlanItem !== undefined ? [selectedPlanItem] : []}
                        onSelectItems={handleSelectPlan}
                    />
                ) : null}
            </WorkflowPage.Card>
        </WorkflowPage>
    )
}

const typeItems = [
    {
        id: 'business',
        title: 'Business',
        subtitle: 'Book, schedule, collect payments, and more for a new business or location',
        tertiaryView: <Icon icon="briefcase.large" background="round" />,
    },
    {
        id: 'mle',
        title: 'Multi-Location Rollup',
        subtitle: 'Rollup multiple businesses to view reports, collect fees, and route leads',
        tertiaryView: <Icon icon="markerPin.large" background="round" />,
    },
]

const planItems = [
    {
        id: 'premium',
        title: `Premium • ${currencyFormat(PREMIUM_PRICE)}`,
        subtitle: 'All the key business features for solo professionals',
        tertiaryView: <Icon icon="briefcase.large" background="round" />,
    },
    {
        id: 'plus',
        title: `Team • ${currencyFormat(PREMIUM_PLUS_PRICE)}`,
        subtitle: 'All the key business features with support for managing your team',
        tertiaryView: <Icon icon="employeeCard.large" background="round" />,
    },
    {
        id: 'suite',
        title: `Enterprise • ${currencyFormat(PREMIUM_SUITE_PRICE)}`,
        subtitle: 'All the key premium features with support for unlimited team members',
        tertiaryView: <Icon icon="diamond.large" background="round" />,
    },
]
